import React, { useEffect, useState } from 'react';
import { Archive, RotateCcw, User, Loader2 } from 'lucide-react';
import C from '../../constants/colors';
import * as employeesApi from '../../api/employees';

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const REASON_LABELS = {
  ODOO_INACTIVE: 'Deactivated in Odoo',
  MANUAL:        'Archived manually',
};

export default function ArchivedEmployeesView({ companies, companyId, onRestored }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setR]   = useState(null);
  const [errorMsg, setError]  = useState('');

  const company = companies.find(c => c.id === companyId);

  useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    try { setRows(await employeesApi.listArchived(companyId ? { companyId } : {})); }
    catch (err) { setError(err.response?.data?.error || 'Failed to load archived employees'); }
    finally { setLoading(false); }
  }

  async function handleRestore(id) {
    if (!window.confirm('Restore this employee? Their record will move back to the active employees list and their QR will work again.')) return;
    setR(id);
    setError('');
    try {
      await employeesApi.restore(id);
      setRows(p => p.filter(e => e.id !== id));
      if (onRestored) await onRestored();
    } catch (err) {
      setError(err.response?.data?.error || 'Restore failed');
    } finally {
      setR(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Archive size={20} color={C.amber} />Archived Employees
          {company && <span style={{ fontSize: '14px', fontWeight: 500, color: C.textMuted }}>· {company.name}</span>}
        </h2>
        <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '6px' }}>
          Terminated or deactivated employees. Their transaction history is preserved but their QR code no longer works. Restore to bring an employee back to active status.
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {loading ? (
          <div style={{ color: C.textMuted, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
            No archived employees.
          </div>
        ) : (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ background: C.surfaceElevated }}>
                <tr>
                  <th style={th}></th>
                  <th style={th}>Name</th>
                  <th style={th}>Employee / Badge ID</th>
                  <th style={th}>Department</th>
                  <th style={th}>Archived</th>
                  <th style={th}>Reason</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(e => (
                  <tr key={e.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ ...td, width: '40px' }}>
                      {e.photoUrl ? (
                        <img src={e.photoUrl} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', filter: 'grayscale(0.7)', opacity: 0.75 }} />
                      ) : (
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: C.surfaceElevated, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <User size={14} color={C.textMuted} />
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, color: C.textPrimary, fontWeight: 600 }}>{e.name}</td>
                    <td style={{ ...td }} className="mono">{e.employeeId || '—'}</td>
                    <td style={td}>{e.department || '—'}</td>
                    <td style={td}>{fmt(e.archivedAt)}</td>
                    <td style={td}>
                      <span style={{ padding: '2px 8px', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', fontSize: '11px', color: C.textSecondary }}>
                        {REASON_LABELS[e.archivedReason] || e.archivedReason || '—'}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => handleRestore(e.id)} disabled={restoringId === e.id}
                        style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.green}55`, background: C.green+'18', color: C.green, fontSize: '12px', fontWeight: 600, cursor: restoringId === e.id ? 'default' : 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '5px', opacity: restoringId === e.id ? 0.6 : 1 }}>
                        {restoringId === e.id ? <Loader2 size={12} className="spin" /> : <RotateCcw size={12} />}
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {errorMsg && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: C.red+'18', border: `1px solid ${C.red}55`, borderRadius: '7px', color: C.red, fontSize: '12px' }}>
            {errorMsg}
          </div>
        )}
      </div>
    </div>
  );
}

const th = { textAlign: 'left', padding: '10px 14px', fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '10px 14px', color: C.textSecondary };
